import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { floorplansAPI, contractsAPI } from '../services/api';
import { ArrowLeft, Save, Printer, RotateCw, Trash2, Copy, ZoomIn, ZoomOut, Grid3X3, Undo2, Redo2, Palette, Layers, Users } from 'lucide-react';

const ROOMS = {
  pub: { name: 'Pub', w: 40, h: 30 },
  banquet_hall: { name: 'Banquet Hall', w: 60, h: 40 },
  lower_patio: { name: 'Lower Patio', w: 50, h: 35 },
  upper_patio: { name: 'Upper Patio', w: 45, h: 30 },
  ceremony_space: { name: 'Ceremony Space', w: 50, h: 60 },
};

const PALETTE = [
  { type: 'round_table_8', label: 'Round (8)', shape: 'circle', w: 72, h: 72, seats: 8, color: '#1e3a5f' },
  { type: 'round_table_10', label: 'Round (10)', shape: 'circle', w: 84, h: 84, seats: 10, color: '#1e3a5f' },
  { type: 'banquet_table', label: 'Banquet', shape: 'rect', w: 120, h: 40, seats: 8, color: '#1e3a5f' },
  { type: 'cocktail_table', label: 'Cocktail', shape: 'circle', w: 40, h: 40, seats: 0, color: '#6b7280' },
  { type: 'head_table', label: 'Head Table', shape: 'rect', w: 200, h: 44, seats: 12, color: '#c9a227' },
  { type: 'bar', label: 'Bar', shape: 'rect', w: 140, h: 44, seats: 0, color: '#7c3aed' },
  { type: 'buffet', label: 'Buffet', shape: 'rect', w: 160, h: 44, seats: 0, color: '#16a34a' },
  { type: 'dj', label: 'DJ', shape: 'rect', w: 70, h: 50, seats: 0, color: '#ec4899' },
  { type: 'dessert', label: 'Dessert', shape: 'rect', w: 90, h: 44, seats: 0, color: '#f59e0b' },
  { type: 'dance_floor', label: 'Dance Floor', shape: 'rect', w: 200, h: 160, seats: 0, color: '#0ea5e9' },
  { type: 'stage', label: 'Stage', shape: 'rect', w: 180, h: 90, seats: 0, color: '#374151' },
  { type: 'photo_booth', label: 'Photo Booth', shape: 'rect', w: 90, h: 70, seats: 0, color: '#a855f7' },
  { type: 'gift_table', label: 'Gift Table', shape: 'rect', w: 90, h: 40, seats: 0, color: '#f97316' },
  { type: 'registration', label: 'Registration', shape: 'rect', w: 120, h: 40, seats: 0, color: '#64748b' },
  { type: 'entrance', label: 'Entrance', shape: 'diamond', w: 36, h: 36, seats: 0, color: '#059669' },
  { type: 'exit', label: 'Exit', shape: 'diamond', w: 36, h: 36, seats: 0, color: '#dc2626' },
  { type: 'custom_label', label: 'Text Label', shape: 'label', w: 80, h: 24, seats: 0, color: '#374151' },
  { type: 'wall', label: 'Wall', shape: 'rect', w: 150, h: 8, seats: 0, color: '#1f2937' },
  { type: 'closet', label: 'Closet', shape: 'rect', w: 70, h: 44, seats: 0, color: '#78716c' },
  { type: 'door', label: 'Door', shape: 'rect', w: 50, h: 12, seats: 0, color: '#a3a3a3' },
  { type: 'window', label: 'Window', shape: 'rect', w: 80, h: 10, seats: 0, color: '#38bdf8' },
];

const GRID_SIZE = 20;

const FloorplanEditor = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);

  const [floorplanId, setFloorplanId] = useState(searchParams.get('id'));
  const [name, setName] = useState('');
  const [room, setRoom] = useState('banquet_hall');
  const [contractId, setContractId] = useState(searchParams.get('contract_id') || '');
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [saving, setSaving] = useState(false);
  const [floorplans, setFloorplans] = useState([]);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [tableCounter, setTableCounter] = useState(1);
  const [colorPickerItem, setColorPickerItem] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [contractsData, floorplansData] = await Promise.all([
        contractsAPI.getAll(), floorplansAPI.getAll(),
      ]);
      setContracts(contractsData);
      setFloorplans(floorplansData);
      if (floorplanId) {
        const fp = await floorplansAPI.getById(floorplanId);
        setName(fp.name); setRoom(fp.room); setContractId(fp.contract_id || '');
        setItems(fp.items || []);
      }
    } catch (err) { console.error(err); }
  };

  const pushHistory = (newItems) => {
    const newHistory = history.slice(0, historyIdx + 1);
    newHistory.push(JSON.parse(JSON.stringify(newItems)));
    setHistory(newHistory);
    setHistoryIdx(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIdx <= 0) return;
    const newIdx = historyIdx - 1;
    setHistoryIdx(newIdx);
    setItems(JSON.parse(JSON.stringify(history[newIdx])));
  };

  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const newIdx = historyIdx + 1;
    setHistoryIdx(newIdx);
    setItems(JSON.parse(JSON.stringify(history[newIdx])));
  };

  const updateItems = (newItems) => {
    setItems(newItems);
    pushHistory(newItems);
  };

  const snap = (val) => snapToGrid ? Math.round(val / GRID_SIZE) * GRID_SIZE : val;

  const handleSave = async () => {
    if (!name.trim()) { toast({ title: 'Enter a floorplan name', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { name, room, contract_id: contractId || null, items };
      if (floorplanId) {
        await floorplansAPI.update(floorplanId, payload);
        toast({ title: 'Floorplan saved!' });
      } else {
        const created = await floorplansAPI.create(payload);
        setFloorplanId(created.id);
        toast({ title: 'Floorplan created!' });
      }
    } catch (err) { toast({ title: 'Error saving', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleLoadFloorplan = async (id) => {
    try {
      const fp = await floorplansAPI.getById(id);
      setFloorplanId(fp.id); setName(fp.name); setRoom(fp.room);
      setContractId(fp.contract_id || ''); setItems(fp.items || []);
      setShowLoadDialog(false);
      toast({ title: `Loaded "${fp.name}"` });
    } catch (err) { toast({ title: 'Error loading', variant: 'destructive' }); }
  };

  const saveAsTemplate = async () => {
    const tplName = `${name || 'Untitled'} (Template)`;
    try {
      await floorplansAPI.create({ name: tplName, room, contract_id: null, items });
      toast({ title: `Saved as template: "${tplName}"` });
      const fps = await floorplansAPI.getAll();
      setFloorplans(fps);
    } catch (err) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const addItem = (paletteItem) => {
    const isTable = paletteItem.seats > 0;
    const num = isTable ? tableCounter : null;
    if (isTable) setTableCounter(prev => prev + 1);
    const newItem = {
      id: crypto.randomUUID(),
      type: paletteItem.type,
      x: 200 + Math.random() * 600,
      y: 100 + Math.random() * 400,
      w: paletteItem.w,
      h: paletteItem.h,
      rotation: 0,
      color: paletteItem.color,
      label: paletteItem.type === 'custom_label' ? 'Label' : (isTable ? `${num}` : null),
      seats: paletteItem.seats,
      tableNum: num,
    };
    const newItems = [...items, newItem];
    updateItems(newItems);
    setSelected([newItem.id]);
  };

  const deleteSelected = () => {
    if (selected.length === 0) return;
    updateItems(items.filter(i => !selected.includes(i.id)));
    setSelected([]);
  };

  const rotateSelected = () => {
    if (selected.length === 0) return;
    updateItems(items.map(i => selected.includes(i.id) ? { ...i, rotation: ((i.rotation || 0) + 45) % 360 } : i));
  };

  const duplicateSelected = () => {
    if (selected.length === 0) return;
    const dupes = items.filter(i => selected.includes(i.id)).map(i => ({
      ...i, id: crypto.randomUUID(), x: i.x + 30, y: i.y + 30
    }));
    const newItems = [...items, ...dupes];
    updateItems(newItems);
    setSelected(dupes.map(d => d.id));
  };

  const resizeSelected = (dw, dh) => {
    updateItems(items.map(i => {
      if (!selected.includes(i.id)) return i;
      return { ...i, w: Math.max(20, (i.w || 60) + dw), h: Math.max(12, (i.h || 60) + dh) };
    }));
  };

  const handleMouseDown = (e, itemId) => {
    e.stopPropagation();
    if (e.shiftKey) {
      setSelected(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
    } else if (!selected.includes(itemId)) {
      setSelected([itemId]);
    }
    const rect = canvasRef.current.getBoundingClientRect();
    setDragging({ startX: e.clientX, startY: e.clientY, rect, itemIds: selected.includes(itemId) ? selected : [itemId] });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    const { startX, startY, rect, itemIds } = dragging;
    const dx = (e.clientX - startX) / zoom;
    const dy = (e.clientY - startY) / zoom;
    setItems(prev => prev.map(i => {
      if (!itemIds.includes(i.id)) return i;
      return { ...i, x: snap(i.x + dx), y: snap(i.y + dy) };
    }));
    setDragging(prev => ({ ...prev, startX: e.clientX, startY: e.clientY }));
  }, [dragging, zoom, snapToGrid]);

  const handleMouseUp = useCallback(() => {
    if (dragging) pushHistory(items);
    setDragging(null);
  }, [dragging, items]);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') { if (selected.length && document.activeElement.tagName !== 'INPUT') deleteSelected(); }
      if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo(); }
      if (e.ctrlKey && e.key === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selected, historyIdx]);

  const totalSeats = items.reduce((sum, i) => sum + (i.seats || 0), 0);
  const getPalette = (type) => PALETTE.find(p => p.type === type) || PALETTE[0];

  const renderItem = (item) => {
    const pal = getPalette(item.type);
    const isSel = selected.includes(item.id);
    const w = item.w || pal.w;
    const h = item.h || pal.h;
    const color = item.color || pal.color;

    const base = {
      position: 'absolute', left: `${item.x}px`, top: `${item.y}px`,
      width: `${w}px`, height: `${h}px`,
      transform: `translate(-50%, -50%) rotate(${item.rotation || 0}deg)`,
      cursor: dragging ? 'grabbing' : 'grab', zIndex: isSel ? 20 : 1,
    };

    const selRing = isSel ? 'ring-2 ring-red-500 ring-offset-1' : '';

    if (pal.shape === 'circle') {
      const diameter = Math.max(w, h);
      return (
        <div key={item.id} style={{ ...base, width: `${diameter}px`, height: `${diameter}px`, borderRadius: '50%', backgroundColor: color }}
          onMouseDown={(e) => handleMouseDown(e, item.id)} className={`flex items-center justify-center text-white text-[10px] font-bold ${selRing}`}>
          {item.label || pal.label}
          {item.seats > 0 && <ChairDots count={item.seats} diameter={diameter} />}
        </div>
      );
    } else if (pal.shape === 'diamond') {
      return (
        <div key={item.id} style={{ ...base, transform: `translate(-50%, -50%) rotate(${(item.rotation || 0) + 45}deg)`, backgroundColor: color }}
          onMouseDown={(e) => handleMouseDown(e, item.id)} className={`flex items-center justify-center text-white text-[9px] font-bold ${selRing}`}>
          <span style={{ transform: `rotate(-${(item.rotation || 0) + 45}deg)` }}>{pal.label.charAt(0)}</span>
        </div>
      );
    } else if (pal.shape === 'label') {
      return (
        <div key={item.id} style={{ ...base, backgroundColor: 'white', border: `2px solid ${color}` }}
          onMouseDown={(e) => handleMouseDown(e, item.id)} className={`flex items-center justify-center text-[10px] font-bold rounded ${selRing}`}>
          {item.label || 'Label'}
        </div>
      );
    } else {
      return (
        <div key={item.id} style={{ ...base, backgroundColor: color, borderRadius: '4px' }}
          onMouseDown={(e) => handleMouseDown(e, item.id)} className={`flex items-center justify-center text-white text-[10px] font-bold ${selRing}`}>
          {item.label || pal.label}
        </div>
      );
    }
  };

  const roomInfo = ROOMS[room] || ROOMS.banquet_hall;
  const canvasW = 1008;
  const canvasH = 612;

  return (
    <div className="min-h-screen bg-[#faf9f6] print:bg-white">
      {/* Toolbar */}
      <div className="print:hidden p-3 border-b bg-white sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8"><ArrowLeft size={14} /></Button>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Floorplan name..." className="w-40 h-8 text-sm" data-testid="floorplan-name" />
            <Select value={room} onValueChange={setRoom}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="floorplan-room-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROOMS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={contractId || 'none'} onValueChange={(v) => setContractId(v === 'none' ? '' : v)}>
              <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="Link contract..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No contract</SelectItem>
                {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.event_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={undo} disabled={historyIdx <= 0} className="h-8 w-8 p-0" title="Undo (Ctrl+Z)"><Undo2 size={14} /></Button>
            <Button variant="outline" size="sm" onClick={redo} disabled={historyIdx >= history.length - 1} className="h-8 w-8 p-0" title="Redo (Ctrl+Y)"><Redo2 size={14} /></Button>
            <span className="mx-1 text-gray-300">|</span>
            <Button variant={showGrid ? 'default' : 'outline'} size="sm" onClick={() => setShowGrid(!showGrid)} className="h-8 w-8 p-0" title="Toggle Grid"><Grid3X3 size={14} /></Button>
            <Button variant={snapToGrid ? 'default' : 'outline'} size="sm" onClick={() => setSnapToGrid(!snapToGrid)} className="h-8 text-xs px-2" title="Snap to Grid">{snapToGrid ? 'Snap ON' : 'Snap'}</Button>
            <span className="mx-1 text-gray-300">|</span>
            <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} className="h-8 w-8 p-0"><ZoomOut size={14} /></Button>
            <span className="text-xs w-10 text-center font-mono">{(zoom * 100).toFixed(0)}%</span>
            <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(2, z + 0.1))} className="h-8 w-8 p-0"><ZoomIn size={14} /></Button>
            <span className="mx-1 text-gray-300">|</span>
            <Button variant="outline" size="sm" onClick={() => setShowLoadDialog(true)} className="h-8 text-xs">Load</Button>
            <Button variant="outline" size="sm" onClick={saveAsTemplate} className="h-8 text-xs">Save Template</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] text-white h-8 text-xs" data-testid="save-floorplan-btn">
              <Save size={14} className="mr-1" /> {saving ? '...' : 'Save'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-8 w-8 p-0"><Printer size={14} /></Button>
          </div>
        </div>

        {/* Selection toolbar */}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 mt-2 py-1.5 px-3 bg-blue-50 border border-blue-200 rounded-lg flex-wrap">
            <span className="text-xs font-medium text-blue-700">{selected.length} selected</span>
            <Button variant="outline" size="sm" onClick={rotateSelected} className="h-7 text-xs"><RotateCw size={12} className="mr-1" /> Rotate</Button>
            <Button variant="outline" size="sm" onClick={duplicateSelected} className="h-7 text-xs"><Copy size={12} className="mr-1" /> Copy</Button>
            <span className="text-gray-300">|</span>
            <span className="text-[10px] text-gray-500">Size:</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => resizeSelected(-10, -10)}>Smaller</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => resizeSelected(10, 10)}>Bigger</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => resizeSelected(20, 0)}>Wider</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => resizeSelected(0, 20)}>Taller</Button>
            {selected.length === 1 && (() => {
              const sel = items.find(i => i.id === selected[0]);
              return (
                <>
                  <span className="text-gray-300">|</span>
                  <Input type="number" className="h-7 w-14 text-xs" value={sel?.w || ''} onChange={(e) => updateItems(items.map(i => i.id === selected[0] ? { ...i, w: parseInt(e.target.value) || 60 } : i))} />
                  <span className="text-[10px]">x</span>
                  <Input type="number" className="h-7 w-14 text-xs" value={sel?.h || ''} onChange={(e) => updateItems(items.map(i => i.id === selected[0] ? { ...i, h: parseInt(e.target.value) || 60 } : i))} />
                  {sel?.type === 'custom_label' && (
                    <Input className="h-7 w-28 text-xs" value={sel?.label || ''} placeholder="Text" onChange={(e) => setItems(items.map(i => i.id === selected[0] ? { ...i, label: e.target.value } : i))} />
                  )}
                  <input type="color" className="h-7 w-7 cursor-pointer border rounded" value={sel?.color || '#1e3a5f'} onChange={(e) => updateItems(items.map(i => i.id === selected[0] ? { ...i, color: e.target.value } : i))} title="Color" />
                </>
              );
            })()}
            <span className="text-gray-300">|</span>
            <Button variant="destructive" size="sm" onClick={deleteSelected} className="h-7 text-xs"><Trash2 size={12} /> Del</Button>
          </div>
        )}
      </div>

      <div className="flex print:block">
        {/* Palette */}
        <div className="print:hidden w-44 bg-white border-r p-2 h-[calc(100vh-110px)] overflow-y-auto sticky top-[110px]">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[10px] font-bold text-[#1e3a5f] uppercase tracking-wide">Items</h3>
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-medium"><Users size={10} className="inline mr-0.5" />{totalSeats} seats</span>
          </div>
          <div className="space-y-1">
            {PALETTE.map(item => (
              <button key={item.type} onClick={() => addItem(item)} data-testid={`palette-${item.type}`}
                className="w-full text-left px-2 py-1.5 text-[11px] rounded border border-gray-200 hover:bg-gray-50 hover:border-[#1e3a5f] transition-colors flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                <span className="flex-1">{item.label}</span>
                {item.seats > 0 && <span className="text-[9px] text-gray-400">{item.seats}</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto p-4 print:p-0 flex items-start justify-center">
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
            <div ref={canvasRef} className="relative bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0 overflow-hidden"
              style={{ width: `${canvasW}px`, height: `${canvasH}px` }}
              onClick={(e) => { if (e.target === canvasRef.current) setSelected([]); }}
              data-testid="floorplan-canvas">
              
              {/* Grid */}
              {showGrid && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" xmlns="http://www.w3.org/2000/svg">
                  <defs><pattern id="grid" width={GRID_SIZE} height={GRID_SIZE} patternUnits="userSpaceOnUse">
                    <path d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`} fill="none" stroke="#94a3b8" strokeWidth="0.5" />
                  </pattern></defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
              )}

              {/* Room outline */}
              <div className="absolute inset-3 border-2 border-dashed border-gray-300 rounded pointer-events-none" />

              {/* Header */}
              <div className="absolute top-1 left-0 right-0 text-center pointer-events-none z-0">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{roomInfo.name}</span>
                {name && <span className="text-[9px] text-gray-400 ml-2">— {name}</span>}
              </div>

              {/* Items */}
              {items.map(item => renderItem(item))}

              {/* Footer */}
              <div className="absolute bottom-1 left-3 right-3 flex justify-between text-[8px] text-gray-300 pointer-events-none">
                <span>Willow Ridge Golf Club</span>
                <span>{totalSeats} seats &middot; {items.length} items</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Load Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Load Floorplan</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {floorplans.length === 0 && <p className="text-sm text-gray-500">No saved floorplans.</p>}
            {floorplans.map(fp => (
              <button key={fp.id} onClick={() => handleLoadFloorplan(fp.id)}
                className="w-full text-left p-3 border rounded hover:bg-gray-50 transition-colors">
                <div className="font-medium text-sm">{fp.name}</div>
                <div className="text-xs text-gray-500">{ROOMS[fp.room]?.name || fp.room} &middot; {fp.items?.length || 0} items</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ChairDots = ({ count, diameter }) => {
  if (!count || count === 0) return null;
  const radius = diameter / 2 - 4;
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * 2 * Math.PI - Math.PI / 2;
        const cx = diameter / 2 + radius * Math.cos(angle) - 3;
        const cy = diameter / 2 + radius * Math.sin(angle) - 3;
        return <div key={i} className="absolute w-1.5 h-1.5 rounded-full bg-white/60" style={{ left: `${cx}px`, top: `${cy}px` }} />;
      })}
    </div>
  );
};

export default FloorplanEditor;
